import {
  client,
  getMachinesSearchTaggingOptions,
  getMeOrganizationsOptions,
  OrgRoles,
  Roles,
} from "@aura-frontend/omnibus-az-client";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  GlobalConfirmationDialog,
  Toaster,
  TooltipProvider,
} from "@win-reality/react-ui";
import React, { lazy, type ReactElement, Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  LoaderFunctionArgs,
  matchRoutes,
  Navigate,
  Outlet,
  redirect,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import magnus from "@/assets/magnus-primary-black.svg";
import AuthenticatedScreen from "@/components/auth/AuthenticatedScreen";
import Layout from "@/components/layout/Layout";
import { TooltipProvider as LocalTooltipProvider } from "@/components/ui/tooltip";
import { configureGameStateClient } from "@/config/game-state-client";
import { supabaseClient } from "@/config/supabase";
import { getHomeRoute } from "@/lib/permissions";
import { queryClient } from "@/lib/queryClient";
import { ROUTES } from "@/lib/routes";
import { addOrgPrefix } from "@/lib/utils";
import PlayerStatsPage from "@/pages/stats/PlayerStats.page";
import { createLoggedFetch } from "@/utils/logged-fetch";
import { registerSupabaseHttpInterceptor } from "@/utils/register-supabase-http-interceptor";
import {
  isHttpClientEvent,
  limitHttpClientErrors,
} from "@/utils/sentry-http-error-limiter";
import { initializeTokenCache } from "@/utils/token-cache";

import { LoadingScreen } from "./components/auth/LoadingScreen";
import MsalMigrationModal from "./components/auth/MsalMigrationModal";
import { AppContextProvider, useAppContext } from "./hooks/use-app-context";
import NotFound from "./pages/404";
import AccountVerificationPage from "./pages/AdminTools/AccountVerification/AccountVerification";
import { Admins } from "./pages/AdminTools/Admins/Admins";
import { AdminToolsLayout } from "./pages/AdminTools/AdminToolsLayout";
import { FindCSV } from "./pages/AdminTools/FindCSV/FindCSV";
import AuthCallback from "./pages/Auth/Callback";
import ClaimPermissionsPage from "./pages/ClaimPermissions/ClaimPermissions";
import CoachMode from "./pages/CoachMode";

const BillingPage = lazy(() => import("./pages/Billing"));
import ChallengeMode from "./pages/ChallengeMode";
import ChallengeModeSession from "./pages/ChallengeMode/ChallengeModeSession";
import ChallengeModeView from "./pages/ChallengeMode/ChallengeModeView";
import CoachModeView from "./pages/CoachMode/CoachModeView";
import CoachSession from "./pages/CoachMode/CoachSession";
import { Complexes } from "./pages/Complexes/Complexes";
import { SingleComplex } from "./pages/Complexes/SingleComplex/SingleComplex";
import { SingleField } from "./pages/Complexes/SingleField/SingleField";
import Dashboard from "./pages/Dashboard";
import { EditEvent } from "./pages/EditEvent/EditEvent";
import EditEventLog from "./pages/EventLog/EditEventLog";
import FindEventLog from "./pages/EventLog/FindEventLog";
import { EventSchedule } from "./pages/EventSchedule/EventSchedule";
import LoginPage from "./pages/Login";
import { ManageLayout, ManageOutlet } from "./pages/Manage/ManageLayout";
import { Operators } from "./pages/Manage/Operators/Operators";
import { Organizations } from "./pages/Manage/Organizations/Organizations";
import SearchPlayersPage from "./pages/Manage/Players/Players";
import { Rosters } from "./pages/Manage/Rosters/Rosters";
import ManageSystemsPage from "./pages/ManageSystems";
import { NewEvent } from "./pages/NewEvent/NewEvent";
import NoAccessPage from "./pages/NoAccess";
import PlayerProfile from "./pages/PlayerProfile";
import { Charts } from "./pages/PlayerProfile/Charts";
import EditProfileSettings from "./pages/PlayerProfile/EditProfile";
import { Home } from "./pages/PlayerProfile/Home";
import Tagging from "./pages/Tagging";
import BattingPracticeModeView from "./pages/Tagging/components/views/BattingPractice";
import BullpenModeView from "./pages/Tagging/components/views/Bullpen";
import GameModeView from "./pages/Tagging/components/views/Game";
import TaggingSession from "./pages/Tagging/TaggingSession";
import Welcome from "./pages/Tagging/Welcome";
import TaggingV2 from "./pages/TaggingV2";
import TaggingV2GameSetup from "./pages/TaggingV2/GameSetup";
import Game from "./pages/TaggingV2/Scorekeeping/Game";
import TaggingV2Welcome from "./pages/TaggingV2/Welcome";

if (!Sentry.getClient()) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
      Sentry.httpClientIntegration({
        failedRequestTargets: [
          import.meta.env.VITE_API_BASE_URL_SPO,
          import.meta.env.VITE_API_BASE_URL_WIN,
        ],
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        // Failed API responses don't trigger a replay, so an API outage can't
        // use up the monthly replay quota
        beforeErrorSampling: (event) => !isHttpClientEvent(event),
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: import.meta.env.VITE_SENTRY_SAMPLE_RATE ?? 0,

    // Only capture Replay for sessions with an error. The Team plan includes
    // 50 replays a month, which random session sampling used up.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Needed for usage tracking via trackEvent
    enableLogs: true,
    environment: import.meta.env.VITE_SENTRY_ENV ?? "staging",
    // Omnibus outages fail every request in every open tab; cap those events
    beforeSend: limitHttpClientErrors,
  });
}

/**
 * Register HTTP interceptors for Supabase authentication.
 *
 * Supabase interceptor:
 * - Attaches Supabase token for authenticated users
 * - Handles 401 recovery via session refresh and anomaly detection
 */
registerSupabaseHttpInterceptor(client);

/**
 * Set the API client base URL to Supabase.
 * All traffic routes to /spo/v1 backend.
 */
client.setConfig({
  ...client.getConfig(),
  baseUrl: import.meta.env.VITE_API_BASE_URL_SPO,
  // The client captured fetch at import, before Sentry.init wrapped it. The
  // logged fetch resolves it per call so requests get Sentry spans,
  // breadcrumbs and error capture, and logs each request's duration.
  // If the API host is ever added to tracePropagationTargets, the API must
  // allow sentry-trace and baggage in CORS or every request will fail.
  fetch: createLoggedFetch("omnibus"),
});

/**
 * Game state (win-game-service) client, routed through the WIN API.
 */
configureGameStateClient();

/**
 * Route-level auth check: requires valid Supabase session.
 * Redirects to /login if not authenticated.
 */
async function checkAuthLoader() {
  // Check Supabase session
  const { data } = await supabaseClient.auth.getSession();
  const hasSupabaseAuth = !!data?.session?.access_token;

  if (!hasSupabaseAuth) {
    return redirect("/login");
  }
}

const RoleBasedHomeRedirect = () => {
  const { user, currentOrganization, userOrganizations, isLoading } =
    useAppContext();

  // Wait for organizations to load before determining home route
  // This prevents redirecting based on incomplete data
  if (isLoading) {
    return <LoadingScreen />;
  }

  const userRoles = (user?.roles || []) as Roles[];
  // If currentOrganization hasn't been set yet but we have organizations,
  // use the first org's roles to get the correct home route
  const firstOrg = userOrganizations?.[0];
  const orgRoles = (currentOrganization?.orgRoles ||
    firstOrg?.orgRoles ||
    []) as OrgRoles[];
  const allRoles = [...userRoles, ...orgRoles];
  const homeRoute = getHomeRoute(allRoles);

  return <Navigate to={homeRoute} replace />;
};

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
export const router = sentryCreateBrowserRouter([
  {
    errorElement: <NotFound />,
  },
  {
    path: ROUTES.NOT_FOUND,
    element: <NotFound />,
  },
  {
    path: "/",
    element: (
      <AuthenticatedScreen>
        <RoleBasedHomeRedirect />
      </AuthenticatedScreen>
    ),
  },
  {
    path: ROUTES.DASHBOARD,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <Dashboard />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      hideNavbar: true,
    },
  },
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
  },
  {
    path: "/auth/callback",
    element: <AuthCallback />,
  },
  {
    path: "/auth/v1/callback",
    element: <AuthCallback />,
  },
  {
    path: "/signup",
    element: <LoginPage isSignup />,
  },
  {
    path: ROUTES.NO_ACCESS,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <NoAccessPage />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      hideNavbar: true,
    },
  },
  {
    path: ROUTES.CLAIM_PERMISSIONS,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <ClaimPermissionsPage />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      label: "Claim Permissions",
      title: "Claim Permissions",
      description: "Link your account to an organization",
    },
  },
  {
    path: "/CMS",
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <>
            <img src={magnus} className="mx-auto size-80" alt="Magnus logo" />
            <h1 className="text-3xl font-bold">CMS</h1>
          </>
        </Layout>
      </AuthenticatedScreen>
    ),
  },
  {
    path: ROUTES.ACCOUNTS,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <Outlet />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      label: "Organizations",
      title: "Organizations",
      description: "Manage organizations and accounts",
      mainClassName: "bg-gray-100",
    },
    children: [
      {
        index: true,
        element: <Organizations />,
        handle: {
          label: "Management",
          title: "Manage Organizations",
        },
      },
      {
        path: ":id",
        handle: {
          label: "Organization Management",
          title: "Manage Organization",
          description: (data: { orgName?: string }) => data?.orgName || "",
        },
        loader: async ({ params }: LoaderFunctionArgs) => {
          try {
            const orgId = addOrgPrefix(params.id!);
            const res = await queryClient.fetchQuery({
              ...getMeOrganizationsOptions(),
              staleTime: 60_000,
            });
            const org = res.userOrganizations?.find((o) => o.orgId === orgId);
            return { orgName: org?.orgName };
          } catch {
            return { orgName: undefined };
          }
        },
        children: [
          {
            index: true,
            element: <div>TO DO: Account Profile</div>,
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.MANAGE,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <ManageLayout />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      hideNavbar: true,
      mainClassName: "bg-gray-100",
    },
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.MANAGE_OPERATORS} replace />,
      },
      {
        path: "rosters",
        element: <ManageOutlet />,
        children: [
          {
            index: true,
            element: <Rosters />,
          },
        ],
      },
      {
        path: "players",
        element: <SearchPlayersPage />,
      },
      {
        path: "operators",
        element: <Operators />,
      },
      {
        path: "organizations",
        element: <ManageOutlet />,
        children: [
          {
            index: true,
            element: <Organizations />,
          },
          {
            path: ":id",
            handle: {
              label: "Organization Management",
              title: "Manage Organization",
              description: (data: { orgName?: string }) => data?.orgName || "",
            },
            loader: async ({ params }: LoaderFunctionArgs) => {
              try {
                const orgId = addOrgPrefix(params.id!);
                const res = await queryClient.fetchQuery({
                  ...getMeOrganizationsOptions(),
                  staleTime: 60_000,
                });
                const org = res.userOrganizations?.find(
                  (o) => o.orgId === orgId,
                );
                return { orgName: org?.orgName };
              } catch {
                return { orgName: undefined };
              }
            },
            children: [
              {
                index: true,
                element: <div>TO DO: Account Profile</div>,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.MANAGE_BILLING,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <Suspense fallback={<LoadingScreen />}>
            <BillingPage />
          </Suspense>
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      label: "Billing",
      title: "Billing & Transactions",
      backPath: ROUTES.MANAGE,
      hideNavbar: true,
      mainClassName: "bg-gray-100",
    },
  },
  {
    path: ROUTES.ADMIN_TOOLS,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <AdminToolsLayout />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      mainClassName: "bg-gray-100",
      hideNavbar: true,
    },
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.ADMIN_TOOLS_ADMINS} replace />,
      },
      {
        path: "admins",
        element: <Admins />,
      },
      {
        path: "account-verification",
        element: <AccountVerificationPage />,
      },
      {
        path: "find-csv",
        element: <FindCSV />,
      },
    ],
  },
  {
    path: ROUTES.MANAGE_SMARTPARKS,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <Outlet />
        </Layout>
      </AuthenticatedScreen>
    ),
    children: [
      {
        index: true,
        element: <Complexes />,
        handle: {
          mainClassName: "bg-gray-100",
          hideNavbar: true,
        },
      },
      {
        path: "complex/:complexName",
        element: <SingleComplex />,
        handle: {
          mainClassName: "bg-gray-100",
          hideNavbar: true,
          backPath: 2,
        },
      },
      {
        path: "complex/:complexName/field/:fieldId",
        element: <SingleField />,
        handle: {
          hideNavbar: true,
        },
      },
    ],
  },
  {
    path: ROUTES.NEW_EVENT,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <NewEvent />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      hideNavbar: true,
      backPath: 1,
    },
  },
  {
    path: ROUTES.EDIT_EVENT,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <EditEvent />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      hideNavbar: true,
      backPath: "history",
    },
  },
  {
    path: ROUTES.MANAGE_SMARTPARKS_ADMIN,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <ManageSystemsPage
            isTaggingMachines={false}
            filters={["region", "search"]}
            columns={[
              "name",
              "machineUuid",
              "region",
              "status",
              "auraAccess",
              "config",
            ]}
          />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      label: "SmartParks",
      title: "SmartParks",
      description: "Manage all SmartParks",
      mainClassName: "bg-gray-100",
    },
  },
  {
    path: ROUTES.REPORTS,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <p>Reports</p>
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      title: "Reports",
      description: "View and download reports",
    },
  },
  {
    path: ROUTES.STATS,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <div className="container">
            <PlayerStatsPage />
          </div>
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      title: "Player Stats",
      description: "View detailed player statistics",
    },
  },
  {
    path: ROUTES.SETTINGS,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <div className="pr-2">
            <EditProfileSettings />
          </div>
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      title: "Settings",
      description: "Edit your profile settings",
    },
  },
  {
    path: ROUTES.TAGGING,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <Outlet />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      title: "Select Your SmartPark",
    },
    children: [
      {
        path: "",
        element: <Tagging />,
        handle: {
          title: "Select Your SmartPark",
        },
      },
      {
        path: ":id",
        element: <TaggingSession />,
        handle: {
          label: "Machine",
          title: (data: { machine?: { name?: string } }) =>
            data?.machine?.name || "Machine",
          description: "What are you trying to score keep or track?",
          navbarClassName: "bg-gray-100",
          mainClassName: "bg-gray-100",
        },
        loader: async ({ params }: LoaderFunctionArgs) => {
          const id = params.id;

          // Check authentication
          const authCheck = await checkAuthLoader();
          if (authCheck) return authCheck;

          const res = await queryClient.fetchQuery({
            ...getMachinesSearchTaggingOptions(),
            staleTime: 60_000,
          });
          const machine = res.machines.find((m) => m.machineUuid === id);

          // Send user back to the tagging page if the machine they are trying to access is inactive
          if (machine && machine.status !== "active") {
            return redirect("/tagging");
          }

          if (!machine) {
            return redirect("/404");
          }

          return { machine };
        },
        children: [
          {
            index: true,
            element: <Welcome />,
            handle: {
              title: (data: { machine?: { name?: string } }) =>
                data?.machine?.name || "Select Mode",
              description: "What are you trying to score keep or track?",
            },
          },
          {
            path: "game",
            element: <GameModeView />,
            handle: {
              label: "Game",
              title: (data: { machine?: { name?: string } }) =>
                data?.machine?.name || "Game",
              description: "Live game tagging",
              sidebarCollapsed: true,
            },
          },
          {
            path: "bullpen",
            element: <BullpenModeView />,
            handle: {
              label: "Bullpen",
              title: (data: { machine?: { name?: string } }) =>
                data?.machine?.name || "Bullpen Session",
              description: "Track bullpen sessions",
              sidebarCollapsed: true,
            },
          },
          {
            path: "batting-practice",
            element: <BattingPracticeModeView />,
            handle: {
              label: "Batting Practice",
              title: (data: { machine?: { name?: string } }) =>
                data?.machine?.name || "Batting Practice",
              description: "Track batting practice sessions",
              sidebarCollapsed: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.TAGGING_V2,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <Outlet />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      title: "Select Your SmartPark",
    },
    children: [
      {
        path: "",
        element: <TaggingV2 />,
        handle: {
          title: "Select Your SmartPark",
        },
      },
      {
        path: ":id",
        element: <Outlet />,
        loader: async ({ params }: LoaderFunctionArgs) => {
          const id = params.id;

          // Check authentication
          const authCheck = await checkAuthLoader();
          if (authCheck) return authCheck;

          const res = await queryClient.fetchQuery({
            ...getMachinesSearchTaggingOptions(),
            staleTime: 60_000,
          });
          const machine = res.machines.find((m) => m.machineUuid === id);

          // Send user back to the tagging page if the machine they are trying to access is inactive
          if (machine && machine.status !== "active") {
            return redirect("/tagging-v2");
          }

          if (!machine) {
            return redirect("/404");
          }

          return { machine };
        },
        children: [
          {
            index: true,
            element: <TaggingV2Welcome />,
            handle: {
              label: "Machine",
              title: (data: { machine?: { name?: string } }) =>
                data?.machine?.name || "Select Mode",
              description: "What are you trying to score keep or track?",
              navbarClassName: "bg-gray-100",
              mainClassName: "bg-gray-100",
            },
          },
          {
            path: "setup",
            element: <TaggingV2GameSetup />,
            handle: {
              label: "Game Setup",
              title: (data: { machine?: { name?: string } }) =>
                data?.machine?.name || "Game Setup",
              description: "Choose the teams and lineups",
              sidebarCollapsed: true,
            },
          },
          {
            path: "game",
            element: <Game />,
            handle: {
              label: "Game",
              title: (data: { machine?: { name?: string } }) =>
                data?.machine?.name || "Game Tagging",
              description: "Live game tagging",
              hideNavbar: true,
              navbarClassName: "bg-gray-100",
              mainClassName: "bg-gray-100",
              sidebarCollapsed: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.LIVE_TRACKING,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <Outlet />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      title: "Select Your SmartPark",
    },
    children: [
      {
        index: true,
        element: <CoachMode />,
        handle: {
          title: "Select Your SmartPark",
        },
      },
      {
        path: ":id",
        element: <CoachSession />,
        handle: {
          label: "Coach Mode",
          title: "Live pitch tracking and metrics",
          description: (data: { machine?: { name?: string } }) =>
            data?.machine?.name || "",
          navbarClassName: "bg-gray-100",
          mainClassName: "bg-gray-100",
          sidebarCollapsed: true,
        },
        loader: async ({ params }: LoaderFunctionArgs) => {
          const id = params.id;

          // Check authentication
          const authCheck = await checkAuthLoader();
          if (authCheck) return authCheck;

          const res = await queryClient.fetchQuery({
            ...getMachinesSearchTaggingOptions(),
            staleTime: 60_000,
          });
          const machine = res.machines.find((m) => m.machineUuid === id);

          if (machine && machine.status !== "active") {
            return redirect("/live-tracking");
          }

          if (!machine) {
            return redirect("/404");
          }

          return { machine };
        },
        children: [
          {
            index: true,
            element: <CoachModeView />,
            handle: {
              title: (data: { machine?: { name?: string } }) =>
                data?.machine?.name || "Coach Mode",
              description: "Live pitch tracking and metrics",
              sidebarCollapsed: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.CHALLENGE_MODE,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <Outlet />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      title: "Select Your SmartPark",
    },
    children: [
      {
        index: true,
        element: <ChallengeMode />,
        handle: {
          title: "Select Your SmartPark",
          description:
            "Challenge mode requires SmartParks to be in Stat Tracking & ABS Challenge System mode",
        },
      },
      {
        path: ":id",
        element: <ChallengeModeSession />,
        handle: {
          label: "Challenge Mode",
          title: "Challenge Mode",
          description: (data: { machine?: { name?: string } }) =>
            data?.machine?.name || "",
          navbarClassName: "bg-gray-100",
          mainClassName: "bg-gray-100",
          sidebarCollapsed: true,
        },
        loader: async ({ params }: LoaderFunctionArgs) => {
          const id = params.id;

          // Check authentication
          const authCheck = await checkAuthLoader();
          if (authCheck) return authCheck;

          const res = await queryClient.fetchQuery({
            ...getMachinesSearchTaggingOptions(),
            staleTime: 60_000,
          });
          const machine = res.machines.find((m) => m.machineUuid === id);

          if (machine && machine.status !== "active") {
            return redirect("/challenge-mode");
          }

          if (!machine) {
            return redirect("/404");
          }

          return { machine };
        },
        children: [
          {
            index: true,
            element: <ChallengeModeView />,
            handle: {
              title: (data: { machine?: { name?: string } }) =>
                data?.machine?.name || "Challenge Mode",
              description: "Challenge Mode",
              sidebarCollapsed: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.EVENT_LOG,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <Outlet />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      label: "Event Log",
      title: "Event Log",
      description: "View and manage event log",
      mainClassName: "bg-gray-100",
    },
    children: [
      {
        index: true,
        element: <FindEventLog />,
        handle: {
          title: "Event Log",
          description: "View and manage event log",
        },
      },
      {
        path: ":gameId",
        element: <EditEventLog />,
        handle: {
          label: "Edit Event",
          title: "Edit Event",
          description: "Review and edit event data",
        },
      },
    ],
  },
  {
    path: ROUTES.EVENT_SCHEDULE,
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <EventSchedule />
        </Layout>
      </AuthenticatedScreen>
    ),
    handle: {
      hideNavbar: true,
      mainClassName: "bg-gray-100",
    },
  },
  {
    path: ROUTES.PLAYER_BY_ID,
    handle: {
      label: "Players",
      title: "Player Profile",
      description: "View player statistics and performance",
      backPath: "history",
    },
    element: (
      <AuthenticatedScreen>
        <Layout authRequired>
          <PlayerProfile />
        </Layout>
      </AuthenticatedScreen>
    ),
    children: [
      {
        path: "",
        element: <Navigate to="home" />,
      },
      {
        path: "home",
        element: <Home />,
        handle: {
          title: "Player Profile",
          description: "View player statistics and performance",
          backPath: "history",
        },
      },
      {
        path: "charts",
        element: <Charts />,
        handle: {
          title: "Player Charts",
          description: "View detailed performance charts",
          backPath: "history",
        },
      },
    ],
  },
]);

const App = (): ReactElement => {
  /**
   * Initialize Supabase token cache on app start.
   * This caches tokens in memory to avoid expensive getSession() calls
   * on every HTTP request (follows MSAL's proven pattern).
   */
  useEffect(() => {
    const unsubscribe = initializeTokenCache(supabaseClient);
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <LocalTooltipProvider>
          <AppContextProvider>
            <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
              <RouterProvider
                router={router}
                fallbackElement={<LoadingScreen />}
              />
            </Sentry.ErrorBoundary>
          </AppContextProvider>
          <Toaster />
          <GlobalConfirmationDialog />
          <MsalMigrationModal />
        </LocalTooltipProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
